/**
 * Map overlay colours. Overlay ink depends on the basemap (light vs dark ground), not the UI
 * theme: white subject on dark/satellite, black subject on daylight.
 */
import type { Aircraft, AircraftRole, Freshness } from '@apos/shared';
import type { BasemapId } from '../../lib/types';

export interface MapPalette {
  light: boolean;
  ink: string;
  inkRgb: string;
  subject: string;
  /** Chase / camera ship ('photo_ship', legacy 'chase'). */
  photo: string;
  /** Watch aircraft ('other'): muted, clearly not the subject. */
  other: string;
  /** Non-mission ADS-B traffic: faint, never competes with mission aircraft. */
  traffic: string;
  camera: string;
  fovFill: string;
  fovLine: string;
  runway: string;
  runwayLine: string;
  ring: string;
  sun: string;
  sunRgb: string;
  crewStale: string;
  halo: string;
  /** 3D ground shadow under an elevated aircraft. */
  shadow: string;
  /** Outline under the elevated 3D glyph: separates it from terrain and sky. */
  glyphOutline: string;
  freshness: Record<Freshness, string | null>;
  marker: Record<string, string>;
  location: string;
  hazard: string;
}

const DARK: MapPalette = {
  light: false,
  ink: '#ffffff',
  inkRgb: '255,255,255',
  subject: '#ffffff',
  photo: '#b69cff',
  other: '#7fb3bd',
  traffic: '#8b929a',
  camera: '#ffffff',
  fovFill: 'rgba(255,255,255,0.18)',
  fovLine: 'rgba(255,255,255,0.55)',
  runway: 'rgba(255,255,255,0.22)',
  runwayLine: 'rgba(255,255,255,0.55)',
  ring: 'rgba(255,255,255,0.42)',
  sun: '#ffb020',
  sunRgb: '255,176,32',
  crewStale: '#7c828a',
  halo: '#ffffff',
  shadow: '#000000',
  glyphOutline: '#000000',
  freshness: { live: null, aging: '#ffc53d', stale: '#ff8a3d', lost: '#ff4d4f', none: '#7c828a' },
  marker: { pass: '#ffffff', hero: '#ffb020', good: '#3ddc84', missed: '#ff8a3d', issue: '#ff4d4f', note: '#c9ced4' },
  location: '#ffffff',
  hazard: '#ff4d4f',
};

const LIGHT: MapPalette = {
  light: true,
  ink: '#000000',
  inkRgb: '0,0,0',
  subject: '#000000',
  photo: '#5b2fd6',
  other: '#1f6b78',
  traffic: '#6e747b',
  camera: '#000000',
  fovFill: 'rgba(0,0,0,0.14)',
  fovLine: 'rgba(0,0,0,0.7)',
  runway: 'rgba(0,0,0,0.3)',
  runwayLine: 'rgba(0,0,0,0.75)',
  ring: 'rgba(0,0,0,0.55)',
  sun: '#b36b00',
  sunRgb: '179,107,0',
  crewStale: '#7a8088',
  halo: '#000000',
  shadow: '#000000',
  glyphOutline: '#ffffff',
  freshness: { live: null, aging: '#9a6a00', stale: '#c04a00', lost: '#d00018', none: '#7a8088' },
  marker: { pass: '#000000', hero: '#b36b00', good: '#00873e', missed: '#c04a00', issue: '#d00018', note: '#3a3f45' },
  location: '#000000',
  hazard: '#d00018',
};

export function paletteFor(b: BasemapId): MapPalette {
  return b === 'daylight' ? LIGHT : DARK;
}

const HEX = /^#[0-9a-f]{6}$/i;

/** The three mission roles as the crew names them: Subject, Chase ('photo_ship' / legacy 'chase'), Watch ('other'). */
export type RoleKind = 'subject' | 'chase' | 'watch';

export function roleKind(role: AircraftRole | null | undefined): RoleKind {
  if (role === 'subject') return 'subject';
  if (role === 'photo_ship' || role === 'chase') return 'chase';
  return 'watch';
}

export const ROLE_LABEL: Record<RoleKind, string> = { subject: 'Subject', chase: 'Chase', watch: 'Watch' };

/** Colour follows the role only, so every Subject / Chase / Watch reads the same on every mission. */
export function aircraftColor(a: Aircraft | undefined, p: MapPalette): string {
  const k = roleKind(a?.role);
  return k === 'subject' ? p.subject : k === 'chase' ? p.photo : p.other;
}

// Recognize explicit helicopter types only; role and labels do not identify an airframe.
const HELI_TYPE = /^(?:bell\s+(?:206|407|429)|b06|b407|b429|as350|h125|h130|h135|h145|ec120|ec130|ec135|ec145|r22|r44|r66|md500|md530|a109|a119|aw109|aw119|aw139|s-?76|uh-60|ah-64|helicopter)$/i;

/** A neutral direction arrow unless the entered type identifies a helicopter, regardless of role. */
export function aircraftShape(a: Aircraft | undefined): 'heli' | 'arrow' {
  const t = (a?.typeDesignator ?? '').trim();
  return HELI_TYPE.test(t) ? 'heli' : 'arrow';
}

export interface RoleStyle {
  /** MapLibre icon-size multiplier (icons are drawn 40 px). */
  iconSize: number;
  /** Freshness ring radius (px). */
  ringR: number;
  /** Draw order (higher on top). */
  z: number;
  /** full = name + alt + speed · medium = name + alt · compact = one line, reg/name + alt */
  label: 'full' | 'medium' | 'compact';
}

export const ROLE_STYLE: Record<RoleKind, RoleStyle> = {
  subject: { iconSize: 1.18, ringR: 23, z: 50, label: 'full' },
  chase: { iconSize: 0.92, ringR: 18, z: 40, label: 'medium' },
  watch: { iconSize: 0.68, ringR: 13, z: 30, label: 'compact' },
};

export function roleStyle(a: Aircraft | undefined): RoleStyle {
  return ROLE_STYLE[roleKind(a?.role)];
}

export function hexToRgb(hex: string): string {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

export function safeHex(c: string | null | undefined, fallback: string): string {
  return c && HEX.test(c) ? c.toLowerCase() : fallback;
}
